/** Marketing i18n fragment — intgTeams (integrations/teams). */

export const intgTeamsEn = {
  meta: {
    title: "Microsoft Teams integration — approvals, ticket questions and governed intake",
    description:
      "Collect approvals on decision cards in Teams channels, answer questions about existing tickets, and route intake into FixControl. The bot follows your Azure app registration, health is tracked per capability, and outbound posts are queued, retried, and audited.",
  },
  hero: {
    eyebrow: "Microsoft Teams integration",
    title: "Governed approvals",
    highlight: "in Microsoft Teams",
    subtitle:
      "Post decision cards for plans, patches, replies, and deployments into the channels your team already watches. Ticket questions get answered in place, intake is routed, and each decision is attributed to a linked identity.",
    primaryCta: "Book a demo",
    secondaryCta: "See ticket questions",
    bullet1: "Cards in your channels",
    bullet2: "Identity-linked decisions",
    bullet3: "Health per capability",
  },
  what: {
    eyebrow: "What it does",
    title: "What the Teams bot handles",
  },
  features: {
    intake: {
      title: "Governed intake",
      body: "An @mention in a channel becomes a work item in the bound FixControl project, scoped to your tenant. The item then follows the normal triage and approval flow.",
    },
    questions: {
      title: "Ticket questions",
      body: "Ask the bot what’s open or waiting for approval across FixControl, Jira, and Freshdesk. A pre-intake intent gate separates questions from new work, so asking about a ticket doesn’t file another one.",
    },
    approvals: {
      title: "Approvals on decision cards",
      body: "Plans, patches, replies, and deployments arrive as decision cards. Approve or request changes on the card; the decision is recorded with actor, role, and channel.",
    },
    sync: {
      title: "Cards stay in sync",
      body: "A decision made in Teams updates the card and the operational timeline together. The channel and the app show the same state.",
    },
    identities: {
      title: "Verified identities",
      body: "Teams users link a FixControl identity before they can decide. Verified and elevated tiers let policy demand stronger identity for riskier approvals.",
    },
    replies: {
      title: "Channel-aware replies",
      body: "The bot replies in the channel where the request came from. In shared or public channels it withholds internal ticket detail, following the tenant’s visibility rules.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "Intake, posting, and identity each report their own health",
    description:
      "A Teams connection is several paths, not one: receiving messages, answering, posting cards, and binding identities can each be healthy or broken on their own. FixControl probes them separately, so a failing outbound path is visible instead of hidden behind a connected badge.",
    receive: {
      name: "Receive channel messages & mentions",
      body: "The bot ingests messages and mentions routed to it and turns them into governed work items in the bound project, inside your own organization.",
    },
    answering: {
      name: "Ticket-question answering",
      body: "Answers questions about existing FixControl, Jira, and Freshdesk tickets, with a gate in front of intake so a question doesn’t create an issue.",
    },
    cards: {
      name: "Approval decision cards",
      body: "Posts decision cards for plans, patches, replies, and deployment gates, and records each approve or request-changes decision with attribution.",
    },
    post: {
      name: "Post replies & update cards",
      body: "Requires the bot to be installed in the team and reachable. Outbound posts are queued in the integration outbox instead of being fired directly at the Teams API.",
    },
    singleTenant: {
      name: "Single-tenant bot configuration",
      body: "The bot can run single-tenant, matching how your Azure app is registered. In that mode it obtains tokens from the tenant where the app is registered, rather than from the multi-tenant Microsoft endpoint.",
    },
    identity: {
      name: "Verified / elevated identity link",
      body: "Available once a Teams user links their FixControl identity. Governance policy can require the elevated tier for higher-risk decisions.",
    },
    greenStatus: {
      name: "One green ‘connected’ checkmark",
      body: "There isn’t one. Posting can fail while intake still works, so FixControl reports each capability separately.",
    },
  },
  failures: {
    eyebrow: "Failure handling",
    title: "Outbound Teams messages either land or get flagged",
    noteTitle: "Queued, retried, recorded",
    noteBody:
      "Outbound Teams writes — replies, card updates, acknowledgements — go through the integration outbox. Transient failures retry; persistent ones dead-letter and show on the operational timeline. Approvals taken in Teams are stored in the decision ledger with actor, role, channel, evidence, and consequence.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Teams-specific questions",
    q1: {
      q: "Is Teams just a chatbot in FixControl?",
      a: "No. The bot is an interface to governed actions: answers, intake, and approvals are each scoped to a project, permission-checked, queued through the outbox, and audited.",
    },
    q2: {
      q: "Does the Teams bot run single-tenant or multi-tenant?",
      a: "It follows your Azure app registration. Registered single-tenant, the bot obtains tokens from that tenant rather than the multi-tenant endpoint — and capability health shows whether the outbound path is working.",
    },
    q3: {
      q: "What happens if a Teams post fails?",
      a: "It stays in the outbox and retries. When retries are exhausted it’s dead-lettered, and the timeline shows the failed post instead of assuming delivery.",
    },
    q4: {
      q: "Who can approve from Teams?",
      a: "Users whose Teams identity is linked to a FixControl identity with the required role. Policy can additionally require the elevated tier for higher-risk actions, and each decision records actor, role, and channel.",
    },
    q5: {
      q: "Are approvals from Teams audited?",
      a: "Yes. A Teams approval is recorded in the decision ledger exactly like an in-app one — same fields, same trail.",
    },
    q6: {
      q: "Can sensitive ticket detail leak into a shared channel?",
      a: "Replies follow tenant isolation and channel visibility. In shared or public channels the bot omits internal ticket detail from what it posts.",
    },
  },
  related: {
    slack: {
      label: "Slack",
      description: "The same approvals and intake for Slack workspaces.",
    },
    questions: {
      label: "Ticket questions",
      description: "Ask about open work without creating more of it.",
    },
    ledger: {
      label: "Approval ledger",
      description: "Where every Teams decision is recorded.",
    },
  },
  cta: {
    title: "Put a decision card in a Teams channel",
    description:
      "Book a demo, approve a change from Teams, and trace the decision in the ledger.",
    primary: "Book a demo",
    secondary: "Ask FixControl",
  },
};

export const intgTeamsNl = {
  meta: {
    title: "Microsoft Teams-integratie — goedkeuringen, ticketvragen en gegoverneerde intake",
    description:
      "Verzamel goedkeuringen op beslis-kaarten in Teams-kanalen, beantwoord vragen over bestaande tickets en route intake naar FixControl. De bot volgt je Azure-app-registratie, health wordt per capability bijgehouden en uitgaande posts worden gewachtrijd, herprobeerd en geauditeerd.",
  },
  hero: {
    eyebrow: "Microsoft Teams-integratie",
    title: "Gegoverneerde goedkeuringen",
    highlight: "in Microsoft Teams",
    subtitle:
      "Plaats beslis-kaarten voor plannen, patches, antwoorden en deployments in de kanalen die je team al volgt. Ticketvragen worden ter plekke beantwoord, intake wordt gerouteerd en elke beslissing is toe te schrijven aan een gekoppelde identiteit.",
    primaryCta: "Boek een demo",
    secondaryCta: "Bekijk ticketvragen",
    bullet1: "Kaarten in je kanalen",
    bullet2: "Identiteit-gekoppelde beslissingen",
    bullet3: "Health per capability",
  },
  what: {
    eyebrow: "Wat het doet",
    title: "Wat de Teams-bot afhandelt",
  },
  features: {
    intake: {
      title: "Gegoverneerde intake",
      body: "Een @mention in een kanaal wordt een werkitem in het gekoppelde FixControl-project, gescoped op je tenant. Het item volgt daarna de normale triage- en goedkeuringsflow.",
    },
    questions: {
      title: "Ticketvragen",
      body: "Vraag de bot wat er openstaat of op goedkeuring wacht in FixControl, Jira en Freshdesk. Een pre-intake intent-gate scheidt vragen van nieuw werk, zodat vragen naar een ticket er niet nog een aanmaakt.",
    },
    approvals: {
      title: "Goedkeuringen op beslis-kaarten",
      body: "Plannen, patches, antwoorden en deployments komen binnen als beslis-kaarten. Keur goed of vraag wijzigingen op de kaart; de beslissing wordt vastgelegd met actor, rol en kanaal.",
    },
    sync: {
      title: "Kaarten blijven gesynchroniseerd",
      body: "Een beslissing in Teams werkt de kaart en de operationele tijdlijn samen bij. Het kanaal en de app tonen dezelfde staat.",
    },
    identities: {
      title: "Geverifieerde identiteiten",
      body: "Teams-gebruikers koppelen een FixControl-identiteit voordat ze kunnen beslissen. Met een geverifieerd en een verhoogd niveau kan beleid een sterkere identiteit eisen voor riskantere goedkeuringen.",
    },
    replies: {
      title: "Kanaal-bewuste antwoorden",
      body: "De bot antwoordt in het kanaal waar het verzoek vandaan kwam. In gedeelde of publieke kanalen houdt hij interne ticketdetails achter, volgens de zichtbaarheidsregels van de tenant.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "Intake, posten en identiteit rapporteren elk hun eigen health",
    description:
      "Een Teams-koppeling is meerdere paden, niet één: berichten ontvangen, beantwoorden, kaarten plaatsen en identiteiten koppelen kunnen elk apart gezond of stuk zijn. FixControl test ze afzonderlijk, zodat een falend uitgaand pad zichtbaar is in plaats van verborgen achter een ‘connected’-badge.",
    receive: {
      name: "Berichten & mentions ontvangen (intake)",
      body: "De bot neemt berichten en mentions op die ernaartoe worden gerouteerd en maakt er gegoverneerde werkitems van in het gekoppelde project, binnen je eigen organisatie.",
    },
    answering: {
      name: "Beantwoorden van ticketvragen",
      body: "Beantwoordt vragen over bestaande FixControl-, Jira- en Freshdesk-tickets, met een gate vóór intake zodat een vraag geen issue aanmaakt.",
    },
    cards: {
      name: "Beslis-kaarten voor goedkeuring",
      body: "Plaatst beslis-kaarten voor plannen, patches, antwoorden en deployment-poorten, en legt elke approve- of request-changes-beslissing vast met attributie.",
    },
    post: {
      name: "Antwoorden & kaart-updates plaatsen",
      body: "Vereist dat de bot in het team is geïnstalleerd en bereikbaar is. Uitgaande posts worden in de integratie-outbox gewachtrijd in plaats van rechtstreeks naar de Teams-API te worden gestuurd.",
    },
    singleTenant: {
      name: "Single-tenant bot-configuratie",
      body: "De bot kan single-tenant draaien, passend bij hoe je Azure-app is geregistreerd. In die modus haalt hij tokens op uit de tenant waar de app is geregistreerd, in plaats van via het multi-tenant Microsoft-endpoint.",
    },
    identity: {
      name: "Geverifieerde / verhoogde identiteitskoppeling",
      body: "Beschikbaar zodra een Teams-gebruiker zijn FixControl-identiteit koppelt. Governance-beleid kan het verhoogde niveau eisen voor beslissingen met hoger risico.",
    },
    greenStatus: {
      name: "Eén groen ‘connected’-vinkje",
      body: "Dat is er niet. Posten kan falen terwijl intake nog werkt, dus FixControl rapporteert elke capability apart.",
    },
  },
  failures: {
    eyebrow: "Foutafhandeling",
    title: "Uitgaande Teams-berichten landen of worden gemarkeerd",
    noteTitle: "Gewachtrijd, herprobeerd, vastgelegd",
    noteBody:
      "Uitgaande Teams-schrijfacties — antwoorden, kaart-updates, bevestigingen — lopen via de integratie-outbox. Tijdelijke fouten worden opnieuw geprobeerd; hardnekkige belanden in de dead-letter en verschijnen op de operationele tijdlijn. Goedkeuringen genomen in Teams worden in het beslis-grootboek bewaard met actor, rol, kanaal, bewijs en consequentie.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Teams-specifieke vragen",
    q1: {
      q: "Is Teams gewoon een chatbot in FixControl?",
      a: "Nee. De bot is een interface naar gegoverneerde acties: antwoorden, intake en goedkeuringen zijn elk gescoped op een project, permissie-getoetst, via de outbox gewachtrijd en geauditeerd.",
    },
    q2: {
      q: "Draait de Teams-bot single-tenant of multi-tenant?",
      a: "Hij volgt je Azure-app-registratie. Single-tenant geregistreerd haalt de bot tokens op uit die tenant in plaats van via het multi-tenant-endpoint — en capability-health laat zien of het uitgaande pad werkt.",
    },
    q3: {
      q: "Wat gebeurt er als een Teams-post mislukt?",
      a: "Hij blijft in de outbox en wordt opnieuw geprobeerd. Zijn de retries op, dan gaat hij naar de dead-letter en toont de tijdlijn de mislukte post in plaats van levering aan te nemen.",
    },
    q4: {
      q: "Wie kan goedkeuren vanuit Teams?",
      a: "Gebruikers van wie de Teams-identiteit is gekoppeld aan een FixControl-identiteit met de vereiste rol. Beleid kan daarnaast het verhoogde niveau eisen voor acties met hoger risico, en elke beslissing legt actor, rol en kanaal vast.",
    },
    q5: {
      q: "Worden goedkeuringen vanuit Teams geaudit?",
      a: "Ja. Een Teams-goedkeuring wordt in het beslis-grootboek vastgelegd, precies als een goedkeuring in-app — dezelfde velden, hetzelfde spoor.",
    },
    q6: {
      q: "Kan gevoelige ticketinformatie in een gedeeld kanaal lekken?",
      a: "Antwoorden volgen tenantisolatie en kanaalzichtbaarheid. In gedeelde of publieke kanalen laat de bot interne ticketdetails weg uit wat hij plaatst.",
    },
  },
  related: {
    slack: {
      label: "Slack",
      description: "Dezelfde goedkeuringen en intake voor Slack-workspaces.",
    },
    questions: {
      label: "Ticketvragen",
      description: "Vraag naar openstaand werk zonder er meer van te maken.",
    },
    ledger: {
      label: "Goedkeurings-grootboek",
      description: "Waar elke Teams-beslissing wordt vastgelegd.",
    },
  },
  cta: {
    title: "Zet een beslis-kaart in een Teams-kanaal",
    description:
      "Boek een demo, keur een wijziging goed vanuit Teams en volg de beslissing in het grootboek.",
    primary: "Boek een demo",
    secondary: "Vraag het FixControl",
  },
};
